import React, { useState } from "react";
import PropTypes from "prop-types";
import Box from "../wrappers/Box";
import Button from "../wrappers/Button";
import FormHelperText from "../wrappers/FormHelperText";
import InputAdornment from "../wrappers/InputAdornment";
import TextField from "../wrappers/TextField";
import Typography from "../wrappers/Typography";
import DropdownFilterChip from "./DropdownFilterChip";

/**
 * Converte a entrada de um campo numérico para o contrato público.
 *
 * @param {string} pValue
 * @returns {number|null}
 */
const pvNormalizeNumber = (pValue) => {
  if (pValue === "") {
    return null;
  }

  const xValue = Number(pValue);

  return Number.isFinite(xValue) ? xValue : null;
};

/**
 * Renderiza um filtro de faixa numérica com rascunho confirmável.
 *
 * @param {object} pProps
 * @param {string} pProps.id Identificador estável do filtro.
 * @param {string} pProps.label Rótulo-base do filtro.
 * @param {{ min: number|null, max: number|null }} pProps.value Faixa confirmada.
 * @param {Function} pProps.onChange Recebe a faixa completa confirmada.
 * @param {string} [pProps.title] Título apresentado dentro do dropdown.
 * @param {number} [pProps.min] Limite absoluto inferior.
 * @param {number} [pProps.max] Limite absoluto superior.
 * @param {number} [pProps.step=1] Incremento dos campos numéricos.
 * @param {string} [pProps.prefix] Prefixo apenas de apresentação.
 * @param {string} [pProps.suffix] Sufixo apenas de apresentação.
 * @param {boolean} [pProps.disabled=false] Desabilita o filtro.
 * @param {Function} [pProps.formatValueLabel] Substitui o resumo padrão.
 * @returns {React.ReactElement}
 */
const DropdownFilterChipRange = (pProps) => {
  const {
    id,
    label,
    value,
    onChange,
    title,
    min,
    max,
    step = 1,
    prefix,
    suffix,
    disabled = false,
    formatValueLabel,
  } = pProps;
  const [open, setOpen] = useState(false);
  const [draftValue, setDraftValue] = useState(() => ({ ...value }));
  const xDescriptionId = `${id}-range-description`;
  const xErrorId = `${id}-range-error`;
  let xErrorMessage;

  if (draftValue.min !== null && draftValue.max !== null && draftValue.min > draftValue.max) {
    xErrorMessage = "O mínimo não pode ser maior que o máximo.";
  } else if (
    min !== undefined &&
    [draftValue.min, draftValue.max].some((pValue) => pValue !== null && pValue < min)
  ) {
    xErrorMessage = `Os valores devem ser maiores ou iguais a ${min}.`;
  } else if (
    max !== undefined &&
    [draftValue.min, draftValue.max].some((pValue) => pValue !== null && pValue > max)
  ) {
    xErrorMessage = `Os valores devem ser menores ou iguais a ${max}.`;
  }

  const xIsInvalid = Boolean(xErrorMessage);
  let xValueLabel;

  if (formatValueLabel) {
    xValueLabel = formatValueLabel(value);
  } else if (value.min !== null && value.max !== null) {
    xValueLabel = `${value.min} – ${value.max}`;
  } else if (value.min !== null) {
    xValueLabel = `A partir de ${value.min}`;
  } else if (value.max !== null) {
    xValueLabel = `Até ${value.max}`;
  }

  /**
   * Copia o valor confirmado ao abrir e descarta o rascunho ao fechar.
   *
   * @param {boolean} pOpen
   */
  const pvHandleOpenChange = (pOpen) => {
    setDraftValue({ ...value });
    setOpen(pOpen);
  };

  /**
   * Atualiza somente um limite do rascunho numérico.
   *
   * @param {"min"|"max"} pKey
   * @param {React.ChangeEvent<HTMLInputElement>} pEvent
   */
  const pvHandleDraftChange = (pKey, pEvent) => {
    setDraftValue((pCurrentValue) => ({
      ...pCurrentValue,
      [pKey]: pvNormalizeNumber(pEvent.target.value),
    }));
  };

  /**
   * Confirma a faixa completa uma única vez quando ela é válida.
   */
  const pvConfirm = () => {
    if (xIsInvalid) {
      return;
    }

    onChange({ ...draftValue });
    setOpen(false);
  };

  return (
    <DropdownFilterChip
      id={id}
      label={label}
      valueLabel={xValueLabel}
      applied={value.min !== null || value.max !== null}
      disabled={disabled}
      open={open}
      onOpenChange={pvHandleOpenChange}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {title && (
          <Typography component="h2" variant="subtitle1">
            {title}
          </Typography>
        )}
        <Typography id={xDescriptionId} variant="body2" color="text.secondary">
          Informe o valor mínimo e máximo da faixa.
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            fullWidth
            type="number"
            label="Mínimo"
            value={draftValue.min ?? ""}
            onChange={(pEvent) => pvHandleDraftChange("min", pEvent)}
            error={xIsInvalid}
            variant="outlined"
            inputProps={{
              min,
              max,
              step,
              "aria-describedby": xIsInvalid ? `${xDescriptionId} ${xErrorId}` : xDescriptionId,
            }}
            InputProps={{
              startAdornment: prefix ? (
                <InputAdornment position="start">{prefix}</InputAdornment>
              ) : undefined,
              endAdornment: suffix ? (
                <InputAdornment position="end">{suffix}</InputAdornment>
              ) : undefined,
            }}
            size="small"
          />
          <TextField
            fullWidth
            type="number"
            label="Máximo"
            value={draftValue.max ?? ""}
            onChange={(pEvent) => pvHandleDraftChange("max", pEvent)}
            error={xIsInvalid}
            variant="outlined"
            inputProps={{
              min,
              max,
              step,
              "aria-describedby": xIsInvalid ? `${xDescriptionId} ${xErrorId}` : xDescriptionId,
            }}
            InputProps={{
              startAdornment: prefix ? (
                <InputAdornment position="start">{prefix}</InputAdornment>
              ) : undefined,
              endAdornment: suffix ? (
                <InputAdornment position="end">{suffix}</InputAdornment>
              ) : undefined,
            }}
            size="small"
          />
        </Box>
        {xIsInvalid && (
          <FormHelperText id={xErrorId} role="alert" error>
            {xErrorMessage}
          </FormHelperText>
        )}
        <Button
          type="button"
          variant="contained"
          disabled={xIsInvalid}
          onClick={pvConfirm}
          size="small"
        >
          Atualizar resultados
        </Button>
      </Box>
    </DropdownFilterChip>
  );
};

DropdownFilterChipRange.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  title: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  disabled: PropTypes.bool,
  formatValueLabel: PropTypes.func,
};

export default DropdownFilterChipRange;
